/**
 * Local copy of @thecampstack/tcs-common's SyncErrorEnvelope contract.
 *
 * Per CEO 2026-05-05 (C4 disposition): tcs-app does NOT yarn-link or depend on
 * @thecampstack/tcs-common. The wire shape is stable (BE owns the contract).
 * If this contract changes, update tcs-common first, then update this file.
 *
 * Source: repos/tcs-common/src/dashboard/sync-error.types.ts
 * See: tcs-cto/feedback_yarn_link_chain_for_local_testing.md
 */

/**
 * Canonical taxonomy of sync errors that can reach the user.
 * Adding a new code is a breaking change to FE consumers — they must add an
 * exhaustive switch arm. Do NOT add codes without architect approval.
 *
 * Plan ref: 2026-05-05-sync-error-handling-and-observability.md §3 D1.
 */
export type SyncErrorCode =
  | 'AUTH_EXPIRED'
  | 'AUTH_INVALID'
  | 'BUSINESS_EMPTY'
  | 'NETWORK'
  | 'PLATFORM'
  | 'RATE_LIMIT'
  | 'SCHEMA_DRIFT'
  | 'TRANSIENT_5XX'

export interface SyncErrorEnvelope {
  /** Machine-readable category. FE switches on this to choose copy + action. */
  code: SyncErrorCode

  /**
   * Optional debug payload. NOT rendered to user. Surfaced in DevTools / Sentry.
   * Engineers must NOT add PII or credentials here — `rawMessage` is the
   * vendor-returned string after the SDK has already redacted credentials.
   */
  debug?: {
    failedRefId?: string
    rawMessage?: string
    rawStatus?: number
  }

  /** Human-readable fallback copy. Populates the legacy `error: string` field for compat. */
  message: string

  /**
   * Vendor-published wait time in seconds. Present iff code === 'RATE_LIMIT'.
   * FE computes `rateLimitedUntil = Date.now() + retryAfterSec * 1000`.
   */
  retryAfterSec?: number

  /**
   * Vendor identifier — 'campminder', 'mailchimp', etc. Drives FE banner copy
   * ("CampMinder is rate-limiting us"). Required even on PLATFORM/NETWORK so
   * the FE can scope the banner to the failed integration.
   */
  vendor: string
}

/** Severity hint emitted by the BE classifier; drives logger.warn vs logger.error + Sentry. */
export type SyncErrorSeverity = 'ERROR' | 'INFO' | 'WARN'

/** Type guard for narrowing `unknown` payloads from socket events. */
export function isSyncErrorEnvelope(value: unknown): value is SyncErrorEnvelope {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    typeof v.code === 'string' &&
    typeof v.message === 'string' &&
    typeof v.vendor === 'string'
  )
}
